import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { projectsApi, roomsApi } from "@/lib/api";
import {
  formatCurrency, calculateWallArea, calculateCeilingArea, calculateBaseboardLength,
  ITEM_LABELS, ITEM_UNITS, PAINT_TYPES, PREP_LEVELS, INTERIOR_CATEGORIES,
  FLAT_PLUS_UNIT_ITEMS, FLAT_PLUS_UNIT_CAVEATS,
} from "@/lib/utils";

export default function RoomEditor() {
  const { projectId, roomId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [projectId, roomId]);

  const loadData = async () => {
    try {
      const projectRes = await projectsApi.getById(projectId);
      setProject(projectRes.data);
      const foundRoom = projectRes.data.rooms.find(r => r.id === roomId);
      if (!foundRoom) { toast.error("Room not found"); navigate(`/project/${projectId}`); return; }
      setRoom(foundRoom);
      const estimateRes = await projectsApi.getEstimate(projectId);
      setEstimate(estimateRes.data);
    } catch (error) {
      toast.error("Failed to load room");
      navigate(`/project/${projectId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDimensionChange = useCallback((field, value) => {
    const numValue = parseFloat(value) || 0;
    setRoom(prev => {
      const updated = { ...prev, [field]: numValue };
      const length = field === 'length' ? numValue : prev.length;
      const width = field === 'width' ? numValue : prev.width;
      const height = field === 'height' ? numValue : prev.height;
      const wallArea = calculateWallArea(length, width, height);
      const ceilingArea = calculateCeilingArea(length, width);
      const baseboardLen = calculateBaseboardLength(length, width);
      const updatedItems = prev.items.map(item => {
        if (item.item_type === 'walls_0_9' || item.item_type === 'walls_9_plus') return { ...item, quantity: wallArea };
        if (item.item_type === 'ceiling_paint') return { ...item, quantity: ceilingArea };
        if (item.item_type === 'baseboards' || item.item_type === 'crown_molding') return { ...item, quantity: baseboardLen };
        return item;
      });
      return { ...updated, items: updatedItems };
    });
  }, []);

  const handleItemChange = useCallback((itemType, field, value) => {
    setRoom(prev => ({
      ...prev,
      items: prev.items.map(item => item.item_type === itemType ? { ...item, [field]: value } : item)
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await roomsApi.update(projectId, roomId, {
        name: room.name, length: room.length, width: room.width, height: room.height, items: room.items, notes: room.notes || "",
      });
      toast.success("Room saved");
      loadData();
    } catch (error) {
      toast.error("Failed to save room");
    } finally {
      setSaving(false);
    }
  };

  const getRoomEstimate = () => {
    if (!estimate?.interior_breakdowns) return null;
    return estimate.interior_breakdowns.find(rb => rb.room_id === roomId);
  };

  const getItemBreakdown = (itemType) => getRoomEstimate()?.item_breakdowns?.find(b => b.item_type === itemType);

  const renderItemGroup = (categoryName, itemTypes, skipHeading = false) => {
    const categoryItems = room.items?.filter(item => itemTypes.includes(item.item_type)) || [];
    if (categoryItems.length === 0) return null;

    return (
      <div key={categoryName} className="space-y-3">
        {!skipHeading && <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2">{categoryName}</h3>}
        {categoryItems.map((item) => {
          const isFlatPlusUnit = FLAT_PLUS_UNIT_ITEMS.includes(item.item_type);
          const breakdown = isFlatPlusUnit ? getItemBreakdown(item.item_type) : null;
          return (
          <div key={item.item_type} className={`p-4 border rounded-sm transition-colors ${item.enabled ? 'border-amber-500/50 bg-card' : 'border-border bg-muted/20'}`} data-testid={`item-${item.item_type}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Switch checked={item.enabled} onCheckedChange={(checked) => handleItemChange(item.item_type, 'enabled', checked)} data-testid={`toggle-${item.item_type}`} />
                <span className="font-medium text-sm">{ITEM_LABELS[item.item_type] || item.item_type}</span>
              </div>
              {item.enabled ? (
                <span className="text-xs px-2 py-1 rounded-sm bg-green-500/20 text-green-400 border border-green-500/30"><Check className="w-3 h-3 inline mr-1" />Included</span>
              ) : (
                <span className="text-xs px-2 py-1 rounded-sm bg-muted text-muted-foreground border border-border"><X className="w-3 h-3 inline mr-1" />Excluded</span>
              )}
            </div>
            {item.enabled && isFlatPlusUnit && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">{FLAT_PLUS_UNIT_CAVEATS[item.item_type]}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="input-group">
                    <Label className="input-label text-xs">Qty ({ITEM_UNITS[item.item_type] || "units"})</Label>
                    <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(item.item_type, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono h-9" data-testid={`quantity-${item.item_type}`} />
                  </div>
                  {breakdown && (
                    <div className="input-group">
                      <Label className="input-label text-xs">Price</Label>
                      <div className="h-9 flex items-center font-mono text-sm">{formatCurrency(breakdown.direct_cost)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {item.enabled && !isFlatPlusUnit && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="input-group">
                  <Label className="input-label text-xs">Qty ({ITEM_UNITS[item.item_type] || "units"})</Label>
                  <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(item.item_type, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono h-9" data-testid={`quantity-${item.item_type}`} />
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Coats</Label>
                  <Select value={String(item.coats)} onValueChange={(v) => handleItemChange(item.item_type, 'coats', parseInt(v))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Coat</SelectItem>
                      <SelectItem value="2">2 Coats</SelectItem>
                      <SelectItem value="3">3 Coats</SelectItem>
                      <SelectItem value="4">4 Coats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Paint Type</Label>
                  <Select value={item.paint_type} onValueChange={(v) => handleItemChange(item.item_type, 'paint_type', v)}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAINT_TYPES.filter(p => !p.value.startsWith('ext')).map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Prep Level</Label>
                  <Select value={String(item.prep_level)} onValueChange={(v) => handleItemChange(item.item_type, 'prep_level', parseInt(v))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{PREP_LEVELS.map(c => <SelectItem key={c.value} value={String(c.value)}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 md:col-span-4 flex items-center gap-3 pt-1">
                  <Switch checked={item.needs_primer} onCheckedChange={(checked) => handleItemChange(item.item_type, 'needs_primer', checked)} />
                  <Label className="text-sm">Needs Primer (+1 coat prime)</Label>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <div className="page-container flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading room...</div></div>;
  if (!room || !project) return null;

  const roomEstimate = getRoomEstimate();
  // Auto-expand any "More items" category that already has an enabled item
  // (e.g. from a room template), so the user sees what's on without a click.
  const moreItemsDefaultOpen = [
    ["Preparation", INTERIOR_CATEGORIES.prep],
    ["Doors", INTERIOR_CATEGORIES.doors],
    ["Windows", INTERIOR_CATEGORIES.windows],
    ["Trim & Molding", INTERIOR_CATEGORIES.trim],
    ["Closets & Cabinetry", INTERIOR_CATEGORIES.other],
  ].filter(([, itemTypes]) => room.items?.some(i => itemTypes.includes(i.item_type) && i.enabled))
   .map(([label]) => label);

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${projectId}`)} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">{room.name}</h1>
              <p className="text-xs text-muted-foreground">{project.name}</p>
            </div>
          </div>
          <Button onClick={handleSave} className="btn-primary" disabled={saving} data-testid="save-room-btn"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Room"}</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Room Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="input-group"><Label className="input-label">Room Name</Label><Input value={room.name} onChange={(e) => setRoom({ ...room, name: e.target.value })} data-testid="room-name-input" /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="input-group"><Label className="input-label">Length (ft)</Label><Input type="number" value={room.length} onChange={(e) => handleDimensionChange('length', e.target.value)} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Width (ft)</Label><Input type="number" value={room.width} onChange={(e) => handleDimensionChange('width', e.target.value)} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Height (ft)</Label><Input type="number" value={room.height} onChange={(e) => handleDimensionChange('height', e.target.value)} className="font-mono" /></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Items to Paint</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {renderItemGroup("Walls & Ceilings", INTERIOR_CATEGORIES.surfaces)}
                <Accordion type="multiple" defaultValue={moreItemsDefaultOpen} className="space-y-0">
                  {[
                    ["Preparation", INTERIOR_CATEGORIES.prep],
                    ["Doors", INTERIOR_CATEGORIES.doors],
                    ["Windows", INTERIOR_CATEGORIES.windows],
                    ["Trim & Molding", INTERIOR_CATEGORIES.trim],
                    ["Closets & Cabinetry", INTERIOR_CATEGORIES.other],
                  ].map(([label, itemTypes]) => {
                    const enabledCount = room.items?.filter(i => itemTypes.includes(i.item_type) && i.enabled).length || 0;
                    return (
                      <AccordionItem key={label} value={label}>
                        <AccordionTrigger className="text-xs uppercase tracking-widest text-muted-foreground font-medium hover:no-underline">
                          <span>{label}{enabledCount > 0 && <span className="ml-2 text-amber-500 normal-case">({enabledCount} included)</span>}</span>
                        </AccordionTrigger>
                        <AccordionContent>{renderItemGroup(label, itemTypes, true)}</AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Room Notes</CardTitle></CardHeader>
              <CardContent><Textarea value={room.notes || ""} onChange={(e) => setRoom({ ...room, notes: e.target.value })} placeholder="Add any special notes..." rows={3} /></CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="sticky-panel">
              <CardHeader><CardTitle className="font-heading text-lg">Room Estimate</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {roomEstimate ? (
                  <>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Dimensions</span><span className="font-mono text-sm">{roomEstimate.dimensions}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Hours</span><span className="font-mono">{roomEstimate.total_hours} hrs</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Cost</span><span className="font-mono">{formatCurrency(roomEstimate.labor_cost)}</span></div>
                    </div>
                    {Object.keys(roomEstimate.material_gallons || {}).length > 0 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Materials</p>
                        <div className="space-y-2">
                          {Object.entries(roomEstimate.material_gallons).map(([type, gallons]) => (
                            <div key={type} className="flex justify-between items-center text-sm"><span className="capitalize">{type.replace('_', ' ')}</span><span className="font-mono">{gallons} gal</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="border-t border-border pt-4"><div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Material Cost</span><span className="font-mono">{formatCurrency(roomEstimate.material_cost)}</span></div></div>
                    <div className="border-t border-border pt-4"><div className="flex justify-between items-center"><span className="font-heading font-semibold">Room Total</span><span className="font-mono text-xl text-amber-500">{formatCurrency(roomEstimate.labor_cost + roomEstimate.material_cost)}</span></div></div>
                    {roomEstimate.item_breakdowns?.length > 0 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Item Breakdown</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {roomEstimate.item_breakdowns.map((item) => (
                            <div key={item.item_type} className="flex justify-between items-center text-sm"><span>{ITEM_LABELS[item.item_type] || item.item_type}</span><span className="font-mono text-muted-foreground">{item.direct_cost > 0 ? formatCurrency(item.direct_cost) : `${item.hours} hrs`}</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4"><p className="text-muted-foreground text-sm">Save room to see estimate</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
